import { PDFDownloadLink, Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';
import { InvestigationIdProps } from "../../../../types/investigation";
import React, { useState } from 'react';
import { useMutation } from "@apollo/client";
import { gqlSchema } from "../../../../gql/schema";
import { message, Select } from "antd";
import * as S from '../styles';
import { BiCollapse, BiCollapseAlt } from 'react-icons/bi';
type Standard = {
    Code: string;
    subject: string;
    description: string;
};

type StandardsData = {
    [key: string]: Standard;
};

const StandardTab: React.FC<
  InvestigationIdProps & {
    isActive?: boolean;
    setFullScreen?: () => void;
    closeFullScreen?: () => void;
    isTeacher?: boolean;
  }
> = ({ investigationId, isActive, setFullScreen, closeFullScreen, isTeacher = false }) => {
    const states = [
        'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California',
        'Colorado', 'Connecticut', 'Delaware', 'District of Columbia', 'Florida',
        'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas',
        'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
        'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada',
        'New Hampshire', 'New Jersey', 'New Mexico', 'New York', 'North Carolina',
        'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island',
        'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont',
        'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming',
    ];

          const handleIconClick = () => {

        if (isActive) {
            if (closeFullScreen) {
                closeFullScreen();
            }
        } else {
            if (setFullScreen) {
                setFullScreen();
            }
        }
    };

    const standards = [
        { value: 'Math', label: 'Math' },
        { value: 'Science', label: 'Science' },
        { value: 'Engineering', label: 'Engineering' },
        { value: 'ELA-reading', label: 'ELA-reading' },
        { value: 'ELA-writing', label: 'ELA-writing' },
        { value: 'ELA-speaking-and-listening', label: 'ELA-speaking and listening' },
        { value: 'Language-development', label: 'Language-development' },
    ];

    const [selectedStandards, setSelectedStandards] = useState<string[]>([]);
    const [selectedState, setSelectedState] = useState<string>('');
    const [altStandardData, setAltStandardData] = useState<StandardsData>({});
    const { Option } = Select;

    const [addAltStandards, { loading }] = useMutation(
        gqlSchema.BookSchema.mutations.AddAltStandards,
        {
            onCompleted: (data) => {
                try {
                    const parsedData = JSON.parse(data.addAllStatesAndStandards.altStandard);
                    const tempData = parsedData[0];
                    setAltStandardData(tempData);
                    message.success('Standards applied successfully!');
                } catch (error) {
                    message.error('Failed to load standards data.');
                }
            },
            onError: (error) => {
                message.error(`Error: ${error.message}`);
            },
        }
    );

    const handleStandardChange = (selectedItems: string[]) => {
        setSelectedStandards(selectedItems);
    };

    const handleStandardSubmit = () => {
        if (!selectedState || selectedStandards.length === 0) {
            message.warning('Please select a state and standards before submitting.');
            return;
        }

        addAltStandards({
            variables: {
                investigationId,
                state: selectedState,
                standard: selectedStandards,
            },
        });
    };

    const styles = StyleSheet.create({
        page: {
            padding: 30,
            fontFamily: 'Helvetica',
            fontSize: 10,
        },
        header: {
            fontSize: 18,
            fontWeight: 'bold',
            marginBottom: 20,
            textAlign: 'center',
        },
        tableRow: {
            flexDirection: 'row',
            borderBottom: '1px solid #ddd',
        },
        tableCell: {
            width: '33.33%',
            padding: 8,
            fontSize: 10,
            border: '1px solid #ddd',
        },
    });

    const generatePDFDocument = () => (
        <Document>
            <Page size="A4" style={styles.page}>
                <Text style={styles.header}>Alt Standards Report</Text>
                <Text>State: {selectedState}</Text>
                <Text>Standards: {selectedStandards.join(', ')}</Text>
                <View>
                    <View style={styles.tableRow}>
                        <Text style={styles.tableCell}>Subject</Text>
                        <Text style={styles.tableCell}>Code</Text>
                        <Text style={styles.tableCell}>Description</Text>
                    </View>
                    {Object.entries(altStandardData).map(([subject, value]) => (
                        <View key={subject} style={styles.tableRow}>
                            <Text style={styles.tableCell}>{value.subject}</Text>
                            <Text style={styles.tableCell}>{value.Code}</Text>
                            <Text style={styles.tableCell}>{value.description}</Text>
                        </View>
                    ))}
                </View>
            </Page>
        </Document>
    );

    return (
        <div style={{ position: 'relative' }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end',alignItems: 'flex-start', marginRight: 30}}>
                <S.Icon isTeacher={isTeacher} onClick={handleIconClick} style={{ marginTop: 10 }}>
                    {isActive ? <BiCollapseAlt /> : <BiCollapse />}
                </S.Icon>
            </div>
        <S.StandardTabContainer>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Select
                    placeholder="Select State"
                    style={{ width: '50%' }}
                    onChange={setSelectedState}
                    value={selectedState || undefined}
                >
                    {states.map((state) => (
                        <Option key={state} value={state}>
                            {state}
                        </Option>
                    ))}
                </Select>
                <Select
                    mode="multiple"
                    placeholder="Select Standards"
                    style={{ width: '300px' }}
                    onChange={handleStandardChange}
                    value={selectedStandards}
                >
                    {standards.map((standard) => (
                        <Option key={standard.value} value={standard.value}>
                            {standard.label}
                        </Option>
                    ))}
                </Select>
                <S.ActionButton onClick={handleStandardSubmit} loading={loading}>
                    View Standards
                </S.ActionButton>
            </div>

            {/* Scrollable List Container */}
            <div
                style={{
                    marginTop: '20px',
                    maxHeight: '300px',
                    overflowY: 'auto', // Enable scrolling
                    border: '1px solid #ddd',
                    padding: '10px',
                }}
            >
                {Object.keys(altStandardData).length > 0 ? (
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr>
                                <th style={{ border: '1px solid #ddd', padding: '8px' }}>Subject</th>
                                <th style={{ border: '1px solid #ddd', padding: '8px' }}>Code</th>
                                <th style={{ border: '1px solid #ddd', padding: '8px' }}>Description</th>
                            </tr>
                        </thead>
                        <tbody>
                            {Object.entries(altStandardData).map(([subject, value]) => (
                                <tr key={subject}>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>{value.subject}</td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>{value.Code}</td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>{value.description}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : (
                    <p>No standards available. Select state and standards to fetch.</p>
                )}
            </div>

            {/* PDF Download Link */}
            <div className="action-btn-container">
                <PDFDownloadLink
                    document={generatePDFDocument()}
                    fileName={`alt_standards_report_${selectedState}.pdf`}
                >
                    {({ loading }) =>
                        loading ? <button>Loading PDF...</button> : <button>Download PDF</button>
                    }
                </PDFDownloadLink>
            </div>
        </S.StandardTabContainer>
      </div>
    );
};

export default StandardTab;